import { useMemo, useState } from "react"
import { toast } from "sonner"
import {
  BankNote03,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Coins03,
  CurrencyDollar as DollarSign,
  DotsHorizontal,
  DownloadCloud01 as Download,
  Edit03,
  InfoCircle,
  ReverseLeft,
  Trophy01,
  Users01 as Users,
  XClose,
  ZapFast,
} from "@untitledui/icons"

import type {
  ApiEstimate,
  ApiTeam,
  TeamMemberSummary,
  TeamSettingsMetadata,
} from "@/lib/api/types"
import type { PayoutRecord } from "@/lib/api/payouts"
import { usePayouts, useApprovePayouts, useUpdatePayoutStatus } from "@/lib/api/use-payouts"
import { resolveActiveAdvisors, withGoalsRewardsDefaults } from "@/lib/dashboard/defaults"
import {
  computeTeamPayouts,
  enumeratePayoutPeriods,
  periodKey,
  type PayoutAdvisorRow,
  type PayoutPeriod,
  type PayoutProofLine,
} from "@/lib/payouts/payout-math"
import { Roles } from "@/lib/team/roles"
import { cx } from "@/lib/utils/cx"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

// Manager-facing payout reconciliation. The screen teaches the workflow it
// runs on — Earn → Review & approve → Mark as paid — and shows, at a glance,
// which periods still need action. Every action is reversible and the one
// consequential step (marking paid) confirms, so a manager can act freely.

interface PayoutsViewProps {
  team: ApiTeam | undefined
  teamId: number | null
  estimates: ApiEstimate[]
  members: TeamMemberSummary[] | undefined
}

const PAST_PERIODS = 6

function money(n: number): string {
  return `$${n.toLocaleString()}`
}

// One consistent primary-action style so every actionable row has the same
// dark button in the same slot — the eye lands on "the next step" every time.
const PRIMARY_BTN =
  "h-9 px-3.5 rounded-lg bg-zinc-900 text-xs font-semibold text-white hover:bg-zinc-800 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"

// Stable identity for a proof line across recomputes — lets a manager exclude
// a specific earning line and have that exclusion survive re-renders (the
// events array is derived fresh each render but is deterministic).
const eventKey = (ev: PayoutProofLine) =>
  `${ev.kind}|${ev.date.getTime()}|${ev.label}|${ev.amount}`

function csvEscape(value: string | number) {
  const s = String(value)
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function downloadCsv(rows: (string | number)[][], filename: string) {
  const blob = new Blob([rows.map((r) => r.map(csvEscape).join(",")).join("\n")], { type: "text/csv" })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

// ── Per-period state, derived for the rail + banner ──────────────────────────
// A period is "accruing" while open. Once closed it's "needs-review" until
// every earning advisor has a decision, then "approved" until every one is
// paid, then "paid". A closed period nobody earned in is "clear".
type PeriodState = "accruing" | "needs-review" | "approved" | "paid" | "clear"

const isSignedOff = (r: PayoutRecord | undefined) =>
  r?.status === "approved" || r?.status === "paid"

function derivePeriodState(
  period: PayoutPeriod,
  rows: PayoutAdvisorRow[],
  recByUser: Map<number, PayoutRecord>,
): PeriodState {
  if (!period.closed) return "accruing"
  // Anyone who earned OR already has a payout record — so a period whose live
  // earnings later drift to $0 still reflects the money that was approved.
  const earners = rows.filter((r) => r.computedTotal > 0 || recByUser.get(r.userId) != null)
  if (earners.length === 0) return "clear"
  const statuses = earners.map((r) => recByUser.get(r.userId)?.status)
  if (statuses.some((s) => s == null || s === "pending_review")) return "needs-review"
  if (statuses.some((s) => s === "approved")) return "approved"
  return "paid"
}

const STATE_META: Record<PeriodState, { label: string; dot: string; text: string }> = {
  accruing: { label: "Still accruing", dot: "bg-blue-500", text: "text-blue-700" },
  "needs-review": { label: "Needs review", dot: "bg-amber-500", text: "text-amber-700" },
  approved: { label: "Approved", dot: "bg-violet-500", text: "text-violet-700" },
  paid: { label: "Paid", dot: "bg-emerald-500", text: "text-emerald-700" },
  clear: { label: "No earnings", dot: "bg-zinc-300", text: "text-zinc-400" },
}

export function PayoutsView({ team, teamId, estimates, members }: PayoutsViewProps) {
  const meta = (team?.metadata ?? {}) as TeamSettingsMetadata
  const advisorMemberCount = useMemo(
    () => (members ?? []).filter((m) => m.role === Roles.ServiceAdvisor).length,
    [members],
  )
  // Advisor count driving per-advisor goal targets — the same resolution the
  // wallet uses, so the bonus math on both sides reconciles.
  const activeAdvisorCount = useMemo(
    () => resolveActiveAdvisors(meta.serviceLane, advisorMemberCount),
    [meta.serviceLane, advisorMemberCount],
  )
  const effective = useMemo(
    () => withGoalsRewardsDefaults(meta.goalsRewards, meta.serviceLane, activeAdvisorCount),
    [meta.goalsRewards, meta.serviceLane, activeAdvisorCount],
  )
  const payoutMode = effective.payoutMode ?? "manual"
  const cadence = effective.payoutCadence ?? "biweekly"
  const cadenceLabel =
    cadence === "weekly" ? "weekly" : cadence === "biweekly" ? "bi-weekly" : "monthly"

  // Snapshot "now" once per mount — period boundaries shouldn't shift
  // between renders while the manager is mid-review.
  const now = useMemo(() => new Date(), [])
  const periods = useMemo(
    () => enumeratePayoutPeriods(cadence, now, PAST_PERIODS),
    [cadence, now],
  )
  const [periodIdx, setPeriodIdx] = useState(0)
  const period = periods[periodIdx]

  const { data: payoutRecords } = usePayouts(teamId)
  const recordsByPeriodStart = useMemo(() => {
    const map = new Map<number, Map<number, PayoutRecord>>()
    for (const r of payoutRecords ?? []) {
      const key = new Date(r.periodStart).getTime()
      let inner = map.get(key)
      if (!inner) {
        inner = new Map()
        map.set(key, inner)
      }
      inner.set(r.userId, r)
    }
    return map
  }, [payoutRecords])

  // Computed rows for every navigable period (cheap — a handful of periods
  // over the team's estimates), so the rail can show each period's total and
  // state without the manager having to open them.
  const periodRows = useMemo(
    () =>
      periods.map((p) =>
        computeTeamPayouts({
          estimates,
          members,
          goalsRewards: effective,
          activeAdvisorCount,
          period: p,
          now,
        }),
      ),
    [periods, estimates, members, effective, activeAdvisorCount, now],
  )
  const periodMeta = useMemo(
    () =>
      periods.map((p, i) => {
        const rows = periodRows[i]
        const recs = recordsByPeriodStart.get(p.start.getTime()) ?? new Map<number, PayoutRecord>()
        const total = rows.reduce(
          (s, r) => s + (recs.get(r.userId)?.approvedAmount ?? r.computedTotal),
          0,
        )
        const needsReviewTotal = rows.reduce(
          (s, r) => (r.computedTotal > 0 && !isSignedOff(recs.get(r.userId)) ? s + r.computedTotal : s),
          0,
        )
        return { total, needsReviewTotal, state: derivePeriodState(p, rows, recs) }
      }),
    [periods, periodRows, recordsByPeriodStart],
  )

  const rows = periodRows[periodIdx]
  const recordByUser =
    recordsByPeriodStart.get(period.start.getTime()) ?? new Map<number, PayoutRecord>()

  const approveMutation = useApprovePayouts(teamId)
  const statusMutation = useUpdatePayoutStatus(teamId)

  const selectedState = periodMeta[periodIdx]?.state ?? "accruing"

  const summary = useMemo(() => {
    let owed = 0
    let earning = 0
    let base = 0
    let bonus = 0
    let competition = 0
    // Split by where the money is in the workflow so the headline reflects
    // what still needs the manager, not the gross (which includes already-
    // paid advisors).
    let toApprove = 0
    let toPay = 0
    let settled = 0
    for (const row of rows) {
      const record = recordByUser.get(row.userId)
      const amt = record?.approvedAmount ?? row.computedTotal
      owed += amt
      if (row.computedTotal > 0) earning++
      base += row.baseScanPay + row.completedPay
      bonus += row.bonusPay
      competition += row.competitionPay
      if (record?.status === "paid") settled += amt
      else if (record?.status === "approved") toPay += amt
      else if (row.computedTotal > 0) toApprove += amt
    }
    return { owed, earning, base, bonus, competition, toApprove, toPay, settled }
  }, [rows, recordByUser])

  // Headline card, framed by the period's state: it leads with the amount
  // that still needs action ("$15 to approve"), not the gross period total.
  const headline = (() => {
    switch (selectedState) {
      case "needs-review":
        return { label: "Needs your review", value: money(summary.toApprove), sub: `of ${money(summary.owed)} this period` }
      case "approved":
        return { label: "Ready to pay", value: money(summary.toPay), sub: `of ${money(summary.owed)} this period` }
      case "paid":
        return { label: "Paid this period", value: money(summary.settled), sub: "all settled" }
      case "accruing":
        return { label: "Total to pay", value: money(summary.owed), sub: "still accruing" }
      default:
        return { label: "Total to pay", value: money(summary.owed), sub: "no earnings this period" }
    }
  })()

  // Where's the work? Count closed periods still needing review + their total.
  const pending = useMemo(() => {
    let count = 0
    let total = 0
    let firstIdx = -1
    periodMeta.forEach((m, i) => {
      if (m.state === "needs-review") {
        count++
        total += m.needsReviewTotal
        if (firstIdx === -1) firstIdx = i
      }
    })
    return { count, total, firstIdx }
  }, [periodMeta])

  const approveItems = (
    targets: { userId: number; approvedAmount: number; note?: string }[],
    successMessage: string,
  ) => {
    if (targets.length === 0) return
    approveMutation.mutate(
      {
        periodStart: period.start.toISOString(),
        periodEnd: period.end.toISOString(),
        items: targets,
      },
      {
        onSuccess: () => toast.success(successMessage),
        onError: () => toast.error("That didn't go through — try again."),
      },
    )
  }

  const setStatus = (record: PayoutRecord, status: PayoutRecord["status"], msg: string) => {
    statusMutation.mutate(
      { payoutId: record.id, status },
      {
        onSuccess: () => toast.success(msg),
        onError: () => toast.error("That didn't go through — try again."),
      },
    )
  }

  const handleExport = () => {
    const csvRows: (string | number)[][] = [
      ["Name", "Email", "Period Start", "Period End", "Scans", "Scheduled", "Completed", "Declined", "Deferred", "Base $", "Bonus $", "Competition $", "Adjustment", "Total Owed"],
    ]
    const emailByUser = new Map((members ?? []).map((m) => [m.id, m.email]))
    for (const row of rows) {
      const record = recordByUser.get(row.userId)
      const owed = record?.approvedAmount ?? row.computedTotal
      csvRows.push([
        row.name,
        emailByUser.get(row.userId) ?? "",
        period.start.toISOString(),
        period.end.toISOString(),
        row.scans,
        row.scheduled,
        row.completed,
        row.declined,
        row.deferredCount,
        row.baseScanPay + row.completedPay,
        row.bonusPay,
        row.competitionPay,
        owed - row.computedTotal,
        owed,
      ])
    }
    downloadCsv(csvRows, `etx-payouts-${periodKey(period)}.csv`)
  }

  return (
    <div className="flex-1 p-4 md:p-8 overflow-auto max-w-[1200px] mx-auto w-full">
      {/* Header */}
      <div className="animate-element flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-5 md:mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">Payouts</h1>
            <span
              className={cx(
                "inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-bold uppercase tracking-wide",
                payoutMode === "auto"
                  ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                  : "bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200",
              )}
            >
              {payoutMode === "auto" ? <ZapFast className="size-3" aria-hidden /> : null}
              {payoutMode === "auto" ? "Auto-approve" : "Manual approval"}
            </span>
          </div>
          <p className="text-sm text-zinc-400 mt-0.5 md:mt-1">
            Pay advisors for the estimates they bring in · {cadenceLabel} periods
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={rows.length === 0}
          className="h-11 px-5 rounded-xl border border-zinc-200 bg-zinc-50 text-sm font-medium text-zinc-700 flex items-center gap-2 hover:bg-white hover:border-zinc-300 transition-all duration-150 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="size-4" />
          Export for payroll
        </button>
      </div>

      {/* Action banner — where's the work */}
      <div
        className={cx(
          "animate-element rounded-2xl px-4 md:px-5 py-3.5 mb-4 flex items-center gap-3",
          pending.count > 0
            ? "border border-amber-200/70 bg-amber-50/70"
            : "border border-emerald-200/70 bg-emerald-50/60",
        )}
      >
        <div
          className={cx(
            "size-8 rounded-lg flex items-center justify-center shrink-0",
            pending.count > 0 ? "bg-amber-100" : "bg-emerald-100",
          )}
        >
          {pending.count > 0 ? (
            <InfoCircle className="size-4 text-amber-600" aria-hidden />
          ) : (
            <CheckCircle className="size-4 text-emerald-600" aria-hidden />
          )}
        </div>
        <p className={cx("text-sm flex-1", pending.count > 0 ? "text-amber-900" : "text-emerald-900")}>
          {pending.count > 0 ? (
            <>
              <span className="font-semibold">
                {pending.count} period{pending.count === 1 ? "" : "s"} need{pending.count === 1 ? "s" : ""} your review
              </span>{" "}
              · {money(pending.total)} to approve
            </>
          ) : (
            <span className="font-semibold">All caught up — nothing waiting on you</span>
          )}
        </p>
        {pending.count > 0 && pending.firstIdx !== periodIdx && (
          <button
            onClick={() => setPeriodIdx(pending.firstIdx)}
            className="h-9 px-3.5 rounded-lg bg-amber-600 text-xs font-semibold text-white hover:bg-amber-700 transition-all duration-150 whitespace-nowrap"
          >
            Review now →
          </button>
        )}
      </div>

      {/* Period rail — every period at a glance */}
      <PeriodRail
        periods={periods}
        periodMeta={periodMeta}
        selectedIdx={periodIdx}
        onSelect={setPeriodIdx}
      />

      {/* Workflow legend */}
      <WorkflowLegend />

      {/* Auto-mode banner */}
      {payoutMode === "auto" && (
        <div className="animate-element rounded-2xl border border-emerald-200/70 bg-emerald-50/60 px-4 md:px-5 py-3.5 mb-5 flex items-center gap-3">
          <div className="size-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
            <ZapFast className="size-4 text-emerald-600" aria-hidden />
          </div>
          <p className="text-sm text-emerald-800">
            Payouts approve themselves at the earned amount when a period closes. You can still
            adjust or undo any row before marking it paid.
          </p>
        </div>
      )}

      {/* Summary strip */}
      <div className="animate-element grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5 md:mb-6">
        {[
          {
            label: headline.label,
            value: headline.value,
            sub: headline.sub,
            icon: DollarSign, iconBg: "bg-emerald-50", iconColor: "text-emerald-600", mono: true,
          },
          {
            label: "Advisors earning",
            value: `${summary.earning}`,
            sub: `of ${rows.length} advisor${rows.length === 1 ? "" : "s"}`,
            icon: Users, iconBg: "bg-blue-50", iconColor: "text-blue-600",
          },
          {
            label: "Base pay",
            value: money(summary.base),
            sub: "per scan + per completed",
            icon: Coins03, iconBg: "bg-violet-50", iconColor: "text-violet-600", mono: true,
          },
          {
            label: "Bonuses & competitions",
            value: money(summary.bonus + summary.competition),
            sub: `${money(summary.competition)} comp · ${money(summary.bonus)} bonus`,
            icon: Trophy01, iconBg: "bg-amber-50", iconColor: "text-amber-600", mono: true,
          },
        ].map((stat, i) => (
          <div
            key={stat.label}
            className="animate-element rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-4 md:p-5 hover:shadow-md transition-all duration-300"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className="flex items-start justify-between mb-3">
              <p className="text-[13px] font-medium text-zinc-500">{stat.label}</p>
              <div className={`size-9 rounded-xl ${stat.iconBg} flex items-center justify-center`}>
                <stat.icon className={`size-5 ${stat.iconColor}`} />
              </div>
            </div>
            <p className={cx("text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight", stat.mono && "tabular-nums")}>
              {stat.value}
            </p>
            {stat.sub && <p className="text-[13px] text-zinc-400 mt-1">{stat.sub}</p>}
          </div>
        ))}
      </div>

      {/* Per-advisor table — keyed by period so drafts reset on navigation. */}
      <PeriodTable
        key={periodKey(period)}
        period={period}
        periodState={selectedState}
        rows={rows}
        recordByUser={recordByUser}
        payoutMode={payoutMode}
        isApproving={approveMutation.isPending}
        isUpdating={statusMutation.isPending}
        onApprove={approveItems}
        onSetStatus={setStatus}
      />
    </div>
  )
}

// ─── period rail ─────────────────────────────────────────────────────────────

function PeriodRail({
  periods,
  periodMeta,
  selectedIdx,
  onSelect,
}: {
  periods: PayoutPeriod[]
  periodMeta: { total: number; needsReviewTotal: number; state: PeriodState }[]
  selectedIdx: number
  onSelect: (i: number) => void
}) {
  // Short "Jun 15–28" range, dropping the redundant repeated month.
  const shortRange = (p: PayoutPeriod) => {
    const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    const s = p.start
    const e = p.end
    if (s.getMonth() === e.getMonth()) return `${MON[s.getMonth()]} ${s.getDate()}–${e.getDate()}`
    return `${MON[s.getMonth()]} ${s.getDate()} – ${MON[e.getMonth()]} ${e.getDate()}`
  }

  return (
    <div className="animate-element rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-2 mb-4">
      <div className="flex items-center gap-1">
        <button
          onClick={() => onSelect(Math.min(periods.length - 1, selectedIdx + 1))}
          disabled={selectedIdx >= periods.length - 1}
          aria-label="Older period"
          className="size-9 shrink-0 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="size-4" aria-hidden />
        </button>

        <div className="flex-1 flex items-stretch gap-1.5 overflow-x-auto px-0.5 py-0.5">
          {periods.map((p, i) => {
            const m = periodMeta[i]
            const sm = STATE_META[m.state]
            const selected = i === selectedIdx
            return (
              <button
                key={periodKey(p)}
                onClick={() => onSelect(i)}
                aria-current={selected}
                className={cx(
                  "shrink-0 min-w-[128px] rounded-xl border px-3 py-2 text-left transition-all duration-150",
                  selected
                    ? "border-zinc-900 bg-zinc-900/[0.03] ring-1 ring-zinc-900"
                    : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50",
                )}
              >
                <div className="flex items-center gap-1.5">
                  <span className={cx("size-1.5 rounded-full shrink-0", sm.dot)} aria-hidden />
                  <span className={cx("text-[11px] font-bold uppercase tracking-wide truncate", sm.text)}>
                    {sm.label}
                  </span>
                </div>
                <div className="text-[13px] font-semibold text-zinc-900 mt-1 whitespace-nowrap">
                  {shortRange(p)}
                </div>
                {/* Needs-review chips lead with the amount that still needs
                    approval, not the gross (which includes settled money). */}
                <div className="text-[12px] text-zinc-400 tabular-nums">
                  {m.state === "clear"
                    ? "—"
                    : m.state === "needs-review"
                      ? `${money(m.needsReviewTotal)} to approve`
                      : money(m.total)}
                </div>
              </button>
            )
          })}
        </div>

        <button
          onClick={() => onSelect(Math.max(0, selectedIdx - 1))}
          disabled={selectedIdx === 0}
          aria-label="Newer period"
          className="size-9 shrink-0 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  )
}

// ─── workflow legend ─────────────────────────────────────────────────────────

function WorkflowLegend() {
  const step = (n: number, label: string) => (
    <span className="inline-flex items-center gap-1.5">
      <span className="size-4 rounded-full bg-zinc-200 text-zinc-600 text-[10px] font-bold grid place-items-center">
        {n}
      </span>
      <span className="text-[12.5px] font-medium text-zinc-600">{label}</span>
    </span>
  )
  return (
    <div className="animate-element flex items-center flex-wrap gap-x-2.5 gap-y-1.5 mb-5 px-1">
      <span className="text-[12.5px] text-zinc-400">How it works:</span>
      {step(1, "Advisor earns")}
      <ChevronRight className="size-3.5 text-zinc-300" aria-hidden />
      {step(2, "You review & approve")}
      <ChevronRight className="size-3.5 text-zinc-300" aria-hidden />
      {step(3, "You mark as paid")}
      <Popover>
        <PopoverTrigger asChild>
          <button
            aria-label="How payouts work"
            className="size-5 rounded-full grid place-items-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <InfoCircle className="size-4" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 text-sm text-zinc-600 leading-relaxed">
          <p className="font-semibold text-zinc-900 mb-1.5">Two steps, both undoable</p>
          <p className="mb-2">
            <span className="font-medium text-zinc-800">Approve</span> signs off the amount you'll
            pay — no money moves yet, and you can adjust or undo it anytime.
          </p>
          <p>
            <span className="font-medium text-zinc-800">Mark as paid</span> records that payroll
            actually sent it. The advisor then sees it as paid in their wallet. You can undo this
            too if payroll didn't go through.
          </p>
        </PopoverContent>
      </Popover>
    </div>
  )
}

// ─── period table ────────────────────────────────────────────────────────────

interface PeriodTableProps {
  period: PayoutPeriod
  periodState: PeriodState
  rows: PayoutAdvisorRow[]
  recordByUser: Map<number, PayoutRecord>
  payoutMode: "manual" | "auto"
  isApproving: boolean
  isUpdating: boolean
  onApprove: (
    targets: { userId: number; approvedAmount: number; note?: string }[],
    successMessage: string,
  ) => void
  onSetStatus: (record: PayoutRecord, status: PayoutRecord["status"], msg: string) => void
}

type Confirmation =
  | { kind: "markPaid"; row: PayoutAdvisorRow; record: PayoutRecord }
  | { kind: "undoPaid"; row: PayoutAdvisorRow; record: PayoutRecord }
  | { kind: "approveAll"; targets: { userId: number; approvedAmount: number; note?: string }[]; total: number }

function PeriodTable({
  period,
  periodState,
  rows,
  recordByUser,
  payoutMode,
  isApproving,
  isUpdating,
  onApprove,
  onSetStatus,
}: PeriodTableProps) {
  const [amountDrafts, setAmountDrafts] = useState<Record<number, string>>({})
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({})
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [adjusting, setAdjusting] = useState<Set<number>>(new Set())
  // Earning lines the manager has struck from an advisor's payout, keyed
  // `${userId}::${eventKey}`. Excluding lines drops their $ off "You pay" so
  // the approved amount only covers what the manager kept.
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  const [confirm, setConfirm] = useState<Confirmation | null>(null)

  const toggleExpanded = (userId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(userId) ? next.delete(userId) : next.add(userId)
      return next
    })

  // Line identity includes the row index so two otherwise-identical proof
  // lines (same kind/date/label/amount) can be struck independently.
  const excludedTotalFor = (row: PayoutAdvisorRow): number =>
    row.events.reduce(
      (s, ev, i) => (excluded.has(`${row.userId}::${i}|${eventKey(ev)}`) ? s + ev.amount : s),
      0,
    )
  const effectiveEarnedFor = (row: PayoutAdvisorRow): number =>
    Math.max(0, row.computedTotal - excludedTotalFor(row))

  // The default amount to pay: a manual override if one's typed, else what's
  // left after any excluded lines.
  const approvedAmountFor = (row: PayoutAdvisorRow): number => {
    const draft = amountDrafts[row.userId]
    if (draft != null && draft.trim() !== "") {
      const parsed = Number(draft)
      if (Number.isFinite(parsed) && parsed >= 0) return parsed
    }
    return effectiveEarnedFor(row)
  }

  const toggleExcludeLine = (userId: number, key: string) =>
    setExcluded((prev) => {
      const next = new Set(prev)
      const k = `${userId}::${key}`
      next.has(k) ? next.delete(k) : next.add(k)
      return next
    })
  const restoreLines = (row: PayoutAdvisorRow) =>
    setExcluded((prev) => {
      const next = new Set(prev)
      row.events.forEach((ev, i) => next.delete(`${row.userId}::${i}|${eventKey(ev)}`))
      return next
    })
  const clearRowEdits = (row: PayoutAdvisorRow) => {
    setAdjusting((prev) => {
      const next = new Set(prev)
      next.delete(row.userId)
      return next
    })
    setAmountDrafts((d) => {
      const { [row.userId]: _drop, ...rest } = d
      return rest
    })
    restoreLines(row)
  }

  const unapproved = rows.filter((row) => !isSignedOff(recordByUser.get(row.userId)) && row.computedTotal > 0)

  const approveRow = (row: PayoutAdvisorRow) => {
    const amount = approvedAmountFor(row)
    const note = noteDrafts[row.userId]?.trim()
    onApprove(
      [{ userId: row.userId, approvedAmount: amount, ...(note ? { note } : {}) }],
      `Approved ${row.name} for ${money(amount)}.`,
    )
    // Reset this row's transient edits — the approved amount is now the record's,
    // so a later revert re-reviews from the real numbers instead of a stale draft.
    clearRowEdits(row)
  }

  const buildApproveAll = (): Confirmation => {
    const targets = unapproved.map((row) => {
      const note = noteDrafts[row.userId]?.trim()
      return { userId: row.userId, approvedAmount: approvedAmountFor(row), ...(note ? { note } : {}) }
    })
    return { kind: "approveAll", targets, total: targets.reduce((s, t) => s + t.approvedAmount, 0) }
  }

  const runConfirm = () => {
    if (!confirm) return
    if (confirm.kind === "approveAll") {
      onApprove(
        confirm.targets,
        `Approved ${confirm.targets.length} advisor${confirm.targets.length === 1 ? "" : "s"} · ${money(confirm.total)}.`,
      )
      // Everything reviewable was just approved — clear all transient edits so
      // no row is stuck in adjust mode and no stale draft lingers.
      setAdjusting(new Set())
      setAmountDrafts({})
      setExcluded(new Set())
    } else if (confirm.kind === "markPaid") {
      onSetStatus(confirm.record, "paid", `Marked ${confirm.row.name} as paid.`)
    } else if (confirm.kind === "undoPaid") {
      onSetStatus(confirm.record, "approved", `${confirm.row.name}'s payout is back to approved.`)
    }
    setConfirm(null)
  }

  const startAdjust = (userId: number) => {
    // Seed the input with the amount currently in play — the record's approved
    // figure for a decided row, else what's left after excluded lines — so a
    // no-change Save can't silently reset it to a different number.
    const row = rows.find((r) => r.userId === userId)
    const rec = recordByUser.get(userId)
    const seed = rec?.approvedAmount ?? (row ? effectiveEarnedFor(row) : 0)
    setAdjusting((prev) => new Set(prev).add(userId))
    setAmountDrafts((d) => ({ ...d, [userId]: String(seed) }))
  }

  const cancelAdjust = (userId: number) => {
    setAdjusting((prev) => {
      const next = new Set(prev)
      next.delete(userId)
      return next
    })
    setAmountDrafts((d) => {
      const { [userId]: _drop, ...rest } = d
      return rest
    })
  }

  return (
    <div className="animate-element rounded-2xl border border-zinc-200/60 bg-white shadow-sm overflow-hidden">
      <div className="px-5 md:px-6 py-4 border-b border-zinc-100 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-base font-semibold text-zinc-900">Advisor payouts · {period.label}</h3>
          <p className="text-sm text-zinc-400 mt-0.5">
            {periodState === "accruing"
              ? "Live preview — you can approve once this period closes"
              : periodState === "clear"
                ? "No earnings to pay this period"
                : "Adjust what you'll pay, then approve and mark paid"}
          </p>
        </div>
        {period.closed && unapproved.length > 0 && (
          <button
            onClick={() => setConfirm(buildApproveAll())}
            disabled={isApproving}
            className="h-10 px-4 rounded-xl bg-zinc-900 text-sm font-semibold text-white flex items-center gap-2 hover:bg-zinc-800 transition-all duration-150 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed self-start"
          >
            {payoutMode === "auto" ? <ZapFast className="size-4" aria-hidden /> : <CheckCircle className="size-4" aria-hidden />}
            Approve all {unapproved.length} · {money(unapproved.reduce((s, r) => s + approvedAmountFor(r), 0))}
          </button>
        )}
      </div>

      {/* Open-period explainer instead of dead-end per-row messaging. */}
      {!period.closed && (
        <div className="px-5 md:px-6 py-3 bg-blue-50/50 border-b border-blue-100/70 text-sm text-blue-800">
          This period is still accruing. Numbers update as advisors work — approvals open when it
          closes on {period.end.toLocaleDateString(undefined, { month: "long", day: "numeric" })}.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50/50">
              <th className="pl-5 md:pl-6 pr-2 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Advisor</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Pipeline</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Earned</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">You pay</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Status</th>
              <th className="pl-3 pr-5 md:pr-6 py-3 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-sm text-zinc-400">
                  No service advisors on this team yet.
                </td>
              </tr>
            )}
            {rows.map((row, index) => {
              const record = recordByUser.get(row.userId)
              return (
                <PayoutRow
                  key={row.userId}
                  row={row}
                  record={record}
                  zebra={index % 2 === 0}
                  isExpanded={expanded.has(row.userId)}
                  onToggle={() => toggleExpanded(row.userId)}
                  periodClosed={period.closed}
                  isAdjusting={adjusting.has(row.userId)}
                  effectiveEarned={effectiveEarnedFor(row)}
                  excludedTotal={excludedTotalFor(row)}
                  isLineExcluded={(key) => excluded.has(`${row.userId}::${key}`)}
                  onToggleLine={(key) => toggleExcludeLine(row.userId, key)}
                  onRestoreLines={() => restoreLines(row)}
                  amountDraft={amountDrafts[row.userId] ?? ""}
                  onAmountChange={(v) => setAmountDrafts((d) => ({ ...d, [row.userId]: v }))}
                  noteDraft={noteDrafts[row.userId] ?? ""}
                  onNoteChange={(v) => setNoteDrafts((d) => ({ ...d, [row.userId]: v }))}
                  isBusy={isApproving || isUpdating}
                  onApprove={() => approveRow(row)}
                  onStartAdjust={() => startAdjust(row.userId)}
                  onCancelAdjust={() => cancelAdjust(row.userId)}
                  onRevert={() =>
                    record && onSetStatus(record, "pending_review", `${row.name}'s payout is back to review.`)
                  }
                  onMarkPaid={() => record && setConfirm({ kind: "markPaid", row, record })}
                  onUndoPaid={() => record && setConfirm({ kind: "undoPaid", row, record })}
                />
              )
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog confirmation={confirm} onCancel={() => setConfirm(null)} onConfirm={runConfirm} />
    </div>
  )
}

// ─── confirm dialog (only the consequential steps) ───────────────────────────

function ConfirmDialog({
  confirmation,
  onCancel,
  onConfirm,
}: {
  confirmation: Confirmation | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const copy = (() => {
    if (!confirmation) return null
    if (confirmation.kind === "markPaid")
      return {
        title: `Mark ${confirmation.row.name} as paid?`,
        body: `This records that payroll sent ${money(confirmation.record.approvedAmount ?? confirmation.record.computedAmount)}. ${confirmation.row.name} will see it as paid in their wallet. You can undo this later.`,
        action: "Mark as paid",
        danger: false,
      }
    if (confirmation.kind === "undoPaid")
      return {
        title: `Undo ${confirmation.row.name}'s payment?`,
        body: `Only do this if payroll didn't actually send it. ${confirmation.row.name} will stop seeing it as paid, and it goes back to approved.`,
        action: "Undo payment",
        danger: true,
      }
    return {
      title: `Approve ${confirmation.targets.length} advisor${confirmation.targets.length === 1 ? "" : "s"}?`,
      body: `You're signing off ${money(confirmation.total)} across ${confirmation.targets.length} advisor${confirmation.targets.length === 1 ? "" : "s"}. No money moves yet — you can still adjust or undo any of them before marking paid.`,
      action: "Approve all",
      danger: false,
    }
  })()

  return (
    <AlertDialog open={confirmation != null} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent>
        {copy && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{copy.title}</AlertDialogTitle>
              <AlertDialogDescription>{copy.body}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onConfirm}
                className={cx(copy.danger && "bg-red-600 hover:bg-red-700 focus:ring-red-600")}
              >
                {copy.action}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ─── row + expandable proof lines ────────────────────────────────────────────

interface PayoutRowProps {
  row: PayoutAdvisorRow
  record: PayoutRecord | undefined
  zebra: boolean
  isExpanded: boolean
  onToggle: () => void
  periodClosed: boolean
  isAdjusting: boolean
  // Earned minus any lines the manager struck; the proposed pay for an
  // unapproved row.
  effectiveEarned: number
  excludedTotal: number
  isLineExcluded: (key: string) => boolean
  onToggleLine: (key: string) => void
  onRestoreLines: () => void
  amountDraft: string
  onAmountChange: (v: string) => void
  noteDraft: string
  onNoteChange: (v: string) => void
  isBusy: boolean
  onApprove: () => void
  onStartAdjust: () => void
  onCancelAdjust: () => void
  onRevert: () => void
  onMarkPaid: () => void
  onUndoPaid: () => void
}

function PayoutRow({
  row,
  record,
  zebra,
  isExpanded,
  onToggle,
  periodClosed,
  isAdjusting,
  effectiveEarned,
  excludedTotal,
  isLineExcluded,
  onToggleLine,
  onRestoreLines,
  amountDraft,
  onAmountChange,
  noteDraft,
  onNoteChange,
  isBusy,
  onApprove,
  onStartAdjust,
  onCancelAdjust,
  onRevert,
  onMarkPaid,
  onUndoPaid,
}: PayoutRowProps) {
  const status = record?.status ?? "pending_review"
  const signedOff = status === "approved" || status === "paid"
  // "You pay" is a clean display by default; it only becomes an input while
  // the manager is explicitly adjusting this one row (from the ⋯ menu).
  const editable = periodClosed && isAdjusting
  const draftAmount =
    amountDraft.trim() !== "" && Number.isFinite(Number(amountDraft)) && Number(amountDraft) >= 0
      ? Number(amountDraft)
      : effectiveEarned
  // While adjusting → the live draft; once decided → the approved figure;
  // otherwise → what's left after excluded lines, as the proposed pay (muted).
  const payAmount = editable
    ? draftAmount
    : signedOff
      ? record?.approvedAmount ?? row.computedTotal
      : effectiveEarned
  // Whether the amount to pay differs from earned — drives only the "reset"
  // affordance while editing; the two amounts sit in adjacent columns, so no
  // extra "over/under earned" text is needed.
  const differsFromEarned = payAmount !== row.computedTotal
  const nothingToPay = row.computedTotal === 0 && !signedOff
  // Lines can be struck only while reviewing (before sign-off) and not while
  // a manual override is being typed.
  const canEditLines = periodClosed && status === "pending_review" && !isAdjusting

  return (
    <>
      <tr className={cx("hover:bg-zinc-50/80 transition-colors duration-100", zebra && "bg-zinc-50/30")}>
        {/* Advisor */}
        <td className="pl-5 md:pl-6 pr-2 py-3.5">
          <button onClick={onToggle} className="flex items-center gap-2 text-left group" aria-expanded={isExpanded}>
            <ChevronDown
              className={cx(
                "size-4 text-zinc-400 group-hover:text-zinc-600 transition-transform duration-150",
                isExpanded && "rotate-180",
              )}
              aria-hidden
            />
            <span className="text-sm font-semibold text-zinc-900 whitespace-nowrap">{row.name}</span>
          </button>
        </td>

        {/* Pipeline — scans → scheduled → done; loss/limbo as quiet context */}
        <td className="px-3 py-3.5">
          <div className="flex items-center gap-1.5 text-sm text-zinc-500 whitespace-nowrap">
            <span className="font-semibold text-zinc-900">{row.scans}</span>
            <span className="text-zinc-400">scans</span>
            <span className="text-zinc-300">→</span>
            <span>{row.scheduled} scheduled</span>
            <span className="text-zinc-300">→</span>
            <span>{row.completed} done</span>
          </div>
          {(row.declined > 0 || row.deferredCount > 0) && (
            <div className="flex items-center gap-2 mt-0.5 text-[11.5px] text-zinc-400">
              {row.declined > 0 && <span>{row.declined} declined</span>}
              {row.deferredCount > 0 && <span>{row.deferredCount} deferred</span>}
            </div>
          )}
        </td>

        {/* Earned — the figure; full breakdown lives in the expand */}
        <td className="px-3 py-3.5 text-right">
          <span className="text-sm font-semibold text-zinc-900 tabular-nums">{money(row.computedTotal)}</span>
        </td>

        {/* You pay — clean display; an input only while adjusting this row */}
        <td className="px-3 py-3.5 text-right">
          {editable ? (
            <div className="flex flex-col items-end gap-1">
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                <input
                  type="number"
                  min={0}
                  autoFocus
                  value={amountDraft}
                  placeholder={String(effectiveEarned)}
                  onChange={(e) => onAmountChange(e.target.value)}
                  aria-label={`Amount to pay ${row.name}`}
                  className="h-9 w-28 rounded-lg border border-zinc-300 bg-white pl-5 pr-2 text-right text-sm tabular-nums text-zinc-900 outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-900/10 transition-all duration-150"
                />
              </div>
              {differsFromEarned && (
                <button
                  onClick={() => onAmountChange("")}
                  className="text-[11px] text-zinc-400 hover:text-zinc-700 whitespace-nowrap"
                  title="Reset to the earned amount"
                >
                  reset to earned
                </button>
              )}
            </div>
          ) : nothingToPay ? (
            <span className="text-sm text-zinc-300">—</span>
          ) : (
            <span className="text-sm font-semibold text-zinc-900 tabular-nums">
              {money(payAmount)}
            </span>
          )}
        </td>

        {/* Status — plain language; muted dash for a $0, undecided row */}
        <td className="px-3 py-3.5">
          {!record && nothingToPay ? (
            <span className="text-sm text-zinc-300">—</span>
          ) : (
            <PlainStatus status={periodClosed ? status : "open"} paidAt={record?.paidAt} />
          )}
        </td>

        {/* Action — one primary step + a ⋯ menu for adjust/undo; fixed right edge */}
        <td className="pl-3 pr-5 md:pr-6 py-3.5 text-right whitespace-nowrap">
          {!periodClosed ? (
            <span className="text-xs text-zinc-400">Not open yet</span>
          ) : editable ? (
            <div className="inline-flex items-center gap-1.5 justify-end">
              <button
                onClick={onCancelAdjust}
                className="h-9 px-3 rounded-lg text-xs font-medium text-zinc-500 hover:bg-zinc-100 transition-colors"
              >
                Cancel
              </button>
              <button onClick={onApprove} disabled={isBusy} className={PRIMARY_BTN}>
                <CheckCircle className="size-3.5" aria-hidden />
                Save
              </button>
            </div>
          ) : nothingToPay ? (
            <span className="text-xs text-zinc-300">Nothing to pay</span>
          ) : (
            <div className="inline-flex items-center gap-1.5 justify-end">
              {status === "pending_review" && (
                <button onClick={onApprove} disabled={isBusy} className={PRIMARY_BTN}>
                  <CheckCircle className="size-3.5" aria-hidden />
                  Approve
                </button>
              )}
              {status === "approved" && (
                <button onClick={onMarkPaid} disabled={isBusy} className={PRIMARY_BTN}>
                  <BankNote03 className="size-3.5" aria-hidden />
                  Mark paid
                </button>
              )}
              {status === "paid" && (
                <span className="text-xs text-zinc-400 mr-1">
                  Paid {record?.paidAt ? shortDate(record.paidAt) : ""}
                </span>
              )}
              <RowMenu
                status={status}
                disabled={isBusy}
                onStartAdjust={onStartAdjust}
                onRevert={onRevert}
                onUndoPaid={onUndoPaid}
              />
            </div>
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr className={cx(zebra && "bg-zinc-50/30")}>
          <td colSpan={6} className="px-5 md:px-6 pb-5 pt-0">
            <div className="rounded-xl border border-zinc-100 bg-zinc-50/60 p-4">
              {row.events.length === 0 ? (
                <p className="text-sm text-zinc-400">No earning events in this period.</p>
              ) : (
                <>
                  {canEditLines ? (
                    <p className="text-[12px] text-zinc-500 mb-2">
                      Remove any line you don't want to pay — it drops off the total.
                    </p>
                  ) : status === "approved" ? (
                    <p className="text-[12px] text-zinc-400 mb-2">
                      Approved.{" "}
                      <button
                        onClick={onRevert}
                        className="font-medium text-zinc-700 hover:text-zinc-900 underline underline-offset-2"
                      >
                        Send back to review
                      </button>{" "}
                      to remove line items.
                    </p>
                  ) : status === "paid" ? (
                    <p className="text-[12px] text-zinc-400 mb-2">
                      Paid.{" "}
                      <button
                        onClick={onUndoPaid}
                        className="font-medium text-zinc-700 hover:text-zinc-900 underline underline-offset-2"
                      >
                        Undo the payment
                      </button>{" "}
                      to remove line items.
                    </p>
                  ) : null}
                  <ul className="divide-y divide-zinc-100">
                    {row.events.map((ev, i) => {
                      const key = `${i}|${eventKey(ev)}`
                      // Strikes only read while reviewing — once approved or
                      // mid-override the line list shows plain amounts.
                      const struck = canEditLines && isLineExcluded(key)
                      const removable = canEditLines && ev.amount > 0
                      return (
                        <li
                          key={i}
                          className={cx(
                            "flex items-center gap-3 py-2 first:pt-0 last:pb-0",
                            struck && "opacity-60",
                          )}
                        >
                          <span
                            className={cx(
                              "text-xs text-zinc-400 tabular-nums w-16 shrink-0",
                              struck && "line-through",
                            )}
                          >
                            {shortDate(ev.date.toISOString())}
                          </span>
                          <span className={cx("text-sm text-zinc-700 truncate", struck && "line-through")}>
                            {ev.label}
                            {ev.vehicleLabel && <span className="text-zinc-400"> · {ev.vehicleLabel}</span>}
                          </span>
                          {ev.kind === "competition" && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wide shrink-0">
                              <Trophy01 className="size-3" aria-hidden />
                              Win
                            </span>
                          )}
                          {ev.kind === "bonus" && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[10px] font-bold uppercase tracking-wide shrink-0">
                              <Coins03 className="size-3" aria-hidden />
                              Bonus
                            </span>
                          )}
                          {ev.duplicateOfVin && (
                            <span className="px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-500 text-[10px] font-bold uppercase tracking-wide shrink-0">
                              duplicate — no payout
                            </span>
                          )}
                          <span
                            className={cx(
                              "ml-auto text-sm font-semibold tabular-nums shrink-0",
                              struck ? "line-through text-zinc-400" : "text-zinc-900",
                            )}
                          >
                            {money(ev.amount)}
                          </span>
                          {removable && (
                            <button
                              onClick={() => onToggleLine(key)}
                              aria-label={struck ? `Restore ${ev.label}` : `Remove ${ev.label}`}
                              title={struck ? "Restore this line" : "Remove — don't pay this line"}
                              className={cx(
                                "shrink-0 size-6 rounded-md grid place-items-center transition-colors",
                                struck
                                  ? "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
                                  : "text-zinc-300 hover:text-red-600 hover:bg-red-50",
                              )}
                            >
                              {struck ? <ReverseLeft className="size-3.5" aria-hidden /> : <XClose className="size-3.5" aria-hidden />}
                            </button>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                  {canEditLines && excludedTotal > 0 && (
                    <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-3">
                      <span className="text-sm text-zinc-600">
                        Removed <span className="font-semibold text-zinc-900">{money(excludedTotal)}</span> —
                        paying <span className="font-semibold text-zinc-900">{money(effectiveEarned)}</span>
                      </span>
                      <button
                        onClick={onRestoreLines}
                        className="text-xs font-medium text-zinc-500 hover:text-zinc-800 transition-colors"
                      >
                        Restore all
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Note travels with the approval — shown as an input while the
                  row is actionable, else the saved note. */}
              {periodClosed && (status === "pending_review" || isAdjusting) ? (
                <div className="mt-3 pt-3 border-t border-zinc-100">
                  <input
                    type="text"
                    value={noteDraft}
                    onChange={(e) => onNoteChange(e.target.value)}
                    placeholder="Add a note for payroll (optional — e.g. why the amount differs)"
                    aria-label={`Payout note for ${row.name}`}
                    className="h-9 w-full max-w-md rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-900 outline-none focus:border-zinc-400 transition-all duration-150"
                  />
                </div>
              ) : record?.note ? (
                <p className="mt-3 pt-3 border-t border-zinc-100 text-sm text-zinc-500">
                  Note: {record.note}
                </p>
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// Secondary actions (adjust / revert / undo) live behind one ⋯ menu so the
// row's right edge stays uniform no matter which state it's in.
function RowMenu({
  status,
  disabled,
  onStartAdjust,
  onRevert,
  onUndoPaid,
}: {
  status: "open" | "pending_review" | "approved" | "paid"
  disabled: boolean
  onStartAdjust: () => void
  onRevert: () => void
  onUndoPaid: () => void
}) {
  const canAdjust = status === "pending_review" || status === "approved"
  if (!canAdjust && status !== "paid") return null
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="More actions"
          disabled={disabled}
          className="size-9 rounded-lg grid place-items-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors disabled:opacity-40"
        >
          <DotsHorizontal className="size-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {canAdjust && (
          <DropdownMenuItem onClick={onStartAdjust}>
            <Edit03 className="size-4 mr-2 text-zinc-400" aria-hidden />
            Adjust amount
          </DropdownMenuItem>
        )}
        {status === "approved" && (
          <DropdownMenuItem onClick={onRevert}>
            <ReverseLeft className="size-4 mr-2 text-zinc-400" aria-hidden />
            Send back to review
          </DropdownMenuItem>
        )}
        {status === "paid" && (
          <DropdownMenuItem onClick={onUndoPaid} className="text-red-600 focus:text-red-700">
            <ReverseLeft className="size-4 mr-2" aria-hidden />
            Undo payment
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Consistent dot + label badge (same grammar as the period rail). Uniform
// height and structure so the Status column reads as one clean stack.
function PlainStatus({
  status,
  paidAt,
}: {
  status: "open" | "pending_review" | "approved" | "paid"
  paidAt?: string | null
}) {
  const meta = {
    open: { label: "Still accruing", dot: "bg-blue-500", bg: "bg-blue-50", text: "text-blue-700" },
    pending_review: { label: "Needs review", dot: "bg-amber-500", bg: "bg-amber-50", text: "text-amber-700" },
    approved: { label: "Approved", dot: "bg-violet-500", bg: "bg-violet-50", text: "text-violet-700" },
    paid: { label: "Paid", dot: "bg-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700" },
  }[status]
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 h-[22px] pl-2 pr-2.5 rounded-full text-[11px] font-semibold whitespace-nowrap",
        meta.bg,
        meta.text,
      )}
      title={
        status === "approved"
          ? "Signed off — ready to pay"
          : status === "paid" && paidAt
            ? `Paid ${shortDate(paidAt)}`
            : undefined
      }
    >
      <span className={cx("size-1.5 rounded-full", meta.dot)} aria-hidden />
      {meta.label}
    </span>
  )
}

function shortDate(iso: string): string {
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return ""
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}
